import type { IAuditLog } from '../engine/AuditLog';
import type { IBatchResult, IOperationLogEntry, OperationStatus } from '../models/Operation';
import type { IScanResult } from '../models/ScanResult';
import { filePath } from '../rules/ruleUtils';
import {
  DOCUMENT_TYPES, hasValue, type IColumnInfo, type ILibraryMetadata, type ITagColumn, type ITagProposal, planColumns, type TagKey
} from './Tagging';

export interface ITaggingEngineOptions {
  scan: IScanResult;
  metadata: ILibraryMetadata;
  audit: IAuditLog;
  user: string;
  now?: () => Date;
  idSuffix?: () => string;
}

function pad(n: number): string {
  return `000${n}`.slice(-4);
}

/**
 * Writes approved tags. Missing columns are created (Document Type as a choice column);
 * a value is written only where the column is empty — values set by hand are never
 * replaced. Every file's write is logged with its previous values, so Undo can clear it.
 */
export class TaggingEngine {
  constructor(private readonly _o: ITaggingEngineOptions) {}

  public async apply(proposals: ITagProposal[], onProgress?: (done: number, total: number) => void): Promise<IBatchResult> {
    const { scan, metadata, audit, user } = this._o;
    const now: () => Date = this._o.now ?? (() => new Date());
    const suffix: string = (this._o.idSuffix ?? (() => Math.random().toString(36).substring(2, 6).toUpperCase()))();
    const started: string = now().toISOString();
    const day: string = started.substring(0, 10).replace(/-/g, '');
    const batchId: string = `TAGS-${day}-${started.substring(11, 19).replace(/:/g, '')}-${suffix}`;
    const result: IBatchResult = { batchId, entries: [] };

    const log = async (entry: Omit<IOperationLogEntry, 'operationId' | 'batchId' | 'user' | 'timestamp' | 'libraryId' | 'libraryName'>): Promise<void> => {
      const full: IOperationLogEntry = {
        ...entry, operationId: `OP-${day}-${suffix}-${pad(result.entries.length + 1)}`, batchId,
        libraryId: scan.library.id, libraryName: scan.library.name, user, timestamp: now().toISOString()
      };
      result.entries.push(full);
      await audit.write(full);
    };

    let columns: { [key in TagKey]: { column: ITagColumn; exists: boolean } };
    try {
      await audit.ensure();
      const existing: IColumnInfo[] = await metadata.getColumns();
      columns = planColumns(existing);
      for (const key of Object.keys(columns) as TagKey[]) {
        if (!columns[key].exists) {
          await metadata.createColumn(columns[key].column, key === 'documentType' ? DOCUMENT_TYPES : undefined);
          await log({
            changeId: '', action: 'createColumn', itemKind: 'folder', itemId: '',
            sourcePath: '', sourceParentId: '', originalName: '',
            destinationPath: scan.library.name, destinationParentId: '', newName: columns[key].column.displayName,
            reason: 'Column created for AI tags.', status: 'success', reversible: false
          });
        }
      }
      // A Document Type column made earlier (or by someone else) may lack newer options,
      // and SharePoint rejects values outside a choice column's list.
      const typeColumn: IColumnInfo | undefined = existing.filter(c => c.name === columns.documentType.column.name)[0];
      if (columns.documentType.exists && typeColumn && typeColumn.type === 'choice' && !typeColumn.allowTextEntry) {
        const have: string[] = typeColumn.choices ?? [];
        const missing: string[] = [];
        for (const p of proposals) {
          const value: string | undefined = p.tags.documentType;
          if (value && !have.concat(missing).some(c => c.toLowerCase() === value.toLowerCase())) {
            missing.push(value);
          }
        }
        if (missing.length > 0) {
          await metadata.setChoices(typeColumn, have.concat(missing));
          await log({
            changeId: '', action: 'createColumn', itemKind: 'folder', itemId: '',
            sourcePath: '', sourceParentId: '', originalName: '',
            destinationPath: scan.library.name, destinationParentId: '', newName: typeColumn.displayName,
            reason: `Added ${missing.map(m => `"${m}"`).join(', ')} to the column's choices.`, status: 'success', reversible: false
          });
        }
      }
    } catch (error) {
      result.abortedReason = `No tags were written. ${String((error as Error)?.message ?? error)}`;
      return result;
    }

    let done: number = 0;
    try {
      for (const proposal of proposals) {
        const file = scan.files[proposal.fileId];
        let status: OperationStatus = 'success';
        let errorMessage: string | undefined;
        const previous: { [column: string]: unknown } = {};
        const written: { [column: string]: unknown } = {};
        try {
          const current: { [column: string]: unknown } = await metadata.getFields(proposal.fileId);
          for (const key of Object.keys(proposal.tags) as TagKey[]) {
            const value: unknown = proposal.tags[key];
            const column: string = columns[key].column.name;
            if (hasValue(value) && !hasValue(current[column])) {
              previous[column] = current[column] ?? null;
              written[column] = value;
            }
          }
          if (Object.keys(written).length === 0) {
            status = 'skipped';
            errorMessage = 'Already tagged — existing values are never replaced.';
          } else {
            await metadata.setFields(proposal.fileId, written);
          }
        } catch (error) {
          status = 'failed';
          errorMessage = String((error as Error)?.message ?? error);
        }
        await log({
          changeId: `tags:${proposal.fileId}`, action: 'tag', itemKind: 'file', itemId: proposal.fileId,
          // For tag entries the "paths" carry the values: before → after, as JSON.
          sourcePath: JSON.stringify(previous), sourceParentId: '', originalName: file ? file.name : '',
          destinationPath: JSON.stringify(written), destinationParentId: '', newName: file ? filePath(file) : '',
          reason: proposal.reason, confidence: proposal.confidence, status, errorMessage,
          reversible: status === 'success'
        });
        done++;
        if (onProgress) {
          onProgress(done, proposals.length);
        }
      }
    } catch (error) {
      result.abortedReason = `Stopped after ${done} of ${proposals.length} files because the audit log could not be written: ` +
        `${String((error as Error)?.message ?? error)}`;
    }
    return result;
  }
}
